import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TitleBar, GoalListView, Select } from '@vyaguta/vyaguta-ui';

import Empty from 'components/common/empty/Empty';
import Loading from 'components/common/loading/Loading';

import { YEAR_OPTIONS } from 'constants/options';
import { GOAL_TYPE, periodOptions } from 'constants/jump';

import { getDefaultFilters, getYearFilters } from 'utils/jump';
import { handleError } from 'utils/errorHandler';

import {
  type GoalFeedback,
  useGoalFeedbacks,
  useUserGoalSummaryQuery,
  useUserGoalsQuery,
  type UserGoalSummary,
} from 'hooks/query/useGoalQueries';

const DEFAULT_COUNT_VALUE = 0;
type GoalFeedbackMap = Record<string, GoalFeedback[]>;

interface GoalQuery {
  year?: string | number;
  period?: string;
  type?: string;
}

interface EmployeeGoalsProps {
  employeeId: number | string;
  query: GoalQuery;
  setQuery: (value: GoalQuery) => void;
}

const EmployeeGoals: React.FC<EmployeeGoalsProps> = ({ employeeId, query, setQuery }) => {
  const TypedTitleBar = TitleBar as React.ComponentType<{
    children: React.ReactNode;
    className?: string;
    disabled?: boolean;
    filterPills?: object[];
  }>;
  const hasRequiredFilters = Boolean(query.year && query.period);
  const goalParams = useMemo(
    () => (hasRequiredFilters ? { type: query.type, ...getYearFilters(query.period, query.year) } : undefined),
    [hasRequiredFilters, query.period, query.type, query.year]
  );
  const goalSummaryParams = useMemo(
    () => (hasRequiredFilters ? getYearFilters(query.period, query.year) : undefined),
    [hasRequiredFilters, query.period, query.year]
  );
  const {
    data: goals = [],
    isFetching: isFetchingUserGoals,
    isError: isUserGoalsError,
    error: userGoalsError,
    errorUpdatedAt: userGoalsErrorUpdatedAt,
  } = useUserGoalsQuery(employeeId, goalParams, hasRequiredFilters);
  const {
    data: goalSummary = [],
    isError: isGoalSummaryError,
    error: goalSummaryError,
    errorUpdatedAt: goalSummaryErrorUpdatedAt,
  } = useUserGoalSummaryQuery(employeeId, goalSummaryParams, hasRequiredFilters);
  const { fetchGoalFeedbacks } = useGoalFeedbacks();
  const [goalFeedbacks, setGoalFeedbacks] = useState<GoalFeedbackMap>({});
  const [isFetchingGoalFeedbacks, setIsFetchingGoalFeedbacks] = useState(false);
  const lastHandledUserGoalsErrorAtRef = useRef(0);
  const lastHandledGoalSummaryErrorAtRef = useRef(0);

  useEffect(() => {
    if (!query.year || !query.period) {
      setQuery(getDefaultFilters());
    }
  }, [query.period, query.year, setQuery]);

  useEffect(() => {
    if (!isUserGoalsError || !userGoalsError || userGoalsErrorUpdatedAt <= lastHandledUserGoalsErrorAtRef.current) {
      return;
    }

    handleError(userGoalsError);
    lastHandledUserGoalsErrorAtRef.current = userGoalsErrorUpdatedAt;
  }, [isUserGoalsError, userGoalsError, userGoalsErrorUpdatedAt]);

  useEffect(() => {
    if (
      !isGoalSummaryError ||
      !goalSummaryError ||
      goalSummaryErrorUpdatedAt <= lastHandledGoalSummaryErrorAtRef.current
    ) {
      return;
    }

    handleError(goalSummaryError);
    lastHandledGoalSummaryErrorAtRef.current = goalSummaryErrorUpdatedAt;
  }, [isGoalSummaryError, goalSummaryError, goalSummaryErrorUpdatedAt]);

  const handleGoalClick = useCallback(
    async (event) => {
      if (event.isOpen && !goalFeedbacks[String(event.goal.id)]) {
        try {
          setIsFetchingGoalFeedbacks(true);
          const fetchedGoalFeedbacks = await fetchGoalFeedbacks(event.goal.id);
          setGoalFeedbacks((previousFeedbacks) => ({
            ...previousFeedbacks,
            [String(event.goal.id)]: fetchedGoalFeedbacks,
          }));
        } catch (error) {
          handleError(error as object);
        } finally {
          setIsFetchingGoalFeedbacks(false);
        }
      }
    },
    [fetchGoalFeedbacks, goalFeedbacks]
  );

  const handlePillClick = useCallback(
    async (_event: object, value: string) => {
      setQuery({ ...query, type: value });
    },
    [query, setQuery]
  );

  const findSummaryCount = useCallback((summary: UserGoalSummary[], type: string) => {
    if (!summary) {
      return DEFAULT_COUNT_VALUE;
    }

    const summaryFound = summary.find((data) => type === data.type);

    return summaryFound ? summaryFound.count : DEFAULT_COUNT_VALUE;
  }, []);

  const [totalCount, goalsCount, achievementsCount] = useMemo(() => {
    if (!goalSummary || !goalSummary.length) {
      return [DEFAULT_COUNT_VALUE, DEFAULT_COUNT_VALUE, DEFAULT_COUNT_VALUE];
    }

    return [
      goalSummary.reduce((acc, cv) => acc + +cv.count, 0),
      findSummaryCount(goalSummary, GOAL_TYPE.GOAL),
      findSummaryCount(goalSummary, GOAL_TYPE.ACHIEVEMENT),
    ];
  }, [goalSummary, findSummaryCount]);

  const filterPills = useMemo(() => {
    return [
      {
        color: 'blue',
        isSmall: false,
        isActive: !query.type,
        notificationCount: totalCount,
        label: 'All',
        value: '',
        onClick: handlePillClick,
        className: false,
      },
      {
        color: 'yellow',
        isSmall: false,
        isActive: query.type === GOAL_TYPE.GOAL,
        notificationCount: goalsCount,
        label: 'Goals',
        value: GOAL_TYPE.GOAL,
        onClick: handlePillClick,
        className: false,
      },
      {
        color: 'green',
        isSmall: false,
        isActive: query.type === GOAL_TYPE.ACHIEVEMENT,
        notificationCount: achievementsCount,
        label: 'Achievements',
        value: GOAL_TYPE.ACHIEVEMENT,
        onClick: handlePillClick,
        className: false,
      },
    ].filter(({ notificationCount }) => Boolean(notificationCount));
  }, [query.type, totalCount, goalsCount, achievementsCount, handlePillClick]);

  const handleYearChange = (option: { value: string | number }) => {
    setQuery({ ...query, year: option.value });
  };

  const handlePeriodChange = (option: { value: string }) => {
    setQuery({ ...query, period: option.value });
  };

  return (
    <div className="container">
      <div className="mt-15">
        <TypedTitleBar className="string mb-20" filterPills={filterPills} disabled={isFetchingUserGoals}>
          <TitleBar.Right>
            {React.createElement(Select as never, {
              isSearchable: true,
              options: periodOptions,
              autoResize: true,
              disabled: isFetchingUserGoals,
              onChange: handlePeriodChange,
              value: periodOptions.find((opt) => opt.value === query.period) || periodOptions[0],
              placeholder: 'Period',
            })}
            {React.createElement(Select as never, {
              isSearchable: true,
              options: YEAR_OPTIONS,
              autoResize: true,
              disabled: isFetchingUserGoals,
              onChange: handleYearChange,
              value: YEAR_OPTIONS.find((opt) => opt.value === +query.year) || YEAR_OPTIONS[0],
              placeholder: 'Year',
            })}
          </TitleBar.Right>
        </TypedTitleBar>
        {isFetchingUserGoals ? (
          <div className="full-scope-card mt-15">
            <Loading />
          </div>
        ) : goals.length ? (
          React.createElement(GoalListView as never, {
            loadingFeedback: isFetchingGoalFeedbacks,
            goals: goals.map((goal) => ({
              goal,
              onClick: handleGoalClick,
              feedbacks: goalFeedbacks[String(goal.id)] || [],
            })),
          })
        ) : (
          <div className="full-scope-card mt-15 p-20 empty-container__wrapper">
            <Empty message="No goals and achievements to show." />
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeGoals;
